import React, { useRef, useState, useEffect } from 'react'
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
} from 'recharts'
import Loading from './components/Loading/Loading'
import { usePortfolioData } from './hooks/usePortfolioData'

const MONTHS = [
	{ short: 'Jan', full: 'January' },
	{ short: 'Feb', full: 'February' },
	{ short: 'Mar', full: 'March' },
	{ short: 'Apr', full: 'April' },
	{ short: 'May', full: 'May' },
	{ short: 'Jun', full: 'June' },
	{ short: 'Jul', full: 'July' },
	{ short: 'Aug', full: 'August' },
	{ short: 'Sep', full: 'September' },
	{ short: 'Oct', full: 'October' },
	{ short: 'Nov', full: 'November' },
	{ short: 'Dec', full: 'December' },
]

const DetailedAnalysis = () => {
	const { portfolioData, loading } = usePortfolioData()
	const [selectedMonths, setSelectedMonths] = useState(() => {
		const now = new Date()
		return [{ month: now.getMonth(), year: now.getFullYear() }]
	})
	const [isFullscreen, setIsFullscreen] = useState(false)
	const chartRef = useRef(null)

	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement)
		}
		document.addEventListener('fullscreenchange', handleFullscreenChange)
		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange)
		}
	}, [])

	const toggleFullscreen = () => {
		if (!document.fullscreenElement) {
			chartRef.current?.requestFullscreen()
		} else {
			document.exitFullscreen()
		}
	}

	const toggleMonth = (month, year) => {
		const key = `${month}-${year}`
        setSelectedMonths(prev => {
            const exists = prev.find(m => `${m.month}-${m.year}` === key);
            let updatedMonths;

            if (exists) {
                updatedMonths = prev.filter(m => `${m.month}-${m.year}` !== key);
            } else {
                updatedMonths = [...prev, { month, year }];
            }

            return updatedMonths.sort((a, b) => (b.year - a.year) || (b.month - a.month));
        })
	}

	const getAvailableMonthsByYear = () => {
		const now = new Date()
		const months = []

		for (let i = 11; i >= 0; i--) {
			const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
			const month = date.getMonth()
			const year = date.getFullYear()

			const hasData = portfolioData.some(item => {
				const itemDate = new Date(item.time)
				return itemDate.getMonth() === month && itemDate.getFullYear() === year
			})

			if (hasData) {
				months.push({ month, year, label: MONTHS[month].short })
			}
		}

		const grouped = {}
		for (const m of months) {
			if (!grouped[m.year]) grouped[m.year] = []
			grouped[m.year].push(m)
		}
		return grouped
	}
	const allSymbols = Array.from(
		new Set(
			portfolioData
				.flatMap(item => JSON.parse(item.value))
				.map(crypto => crypto.symbol)
		)
	).sort()

	const symbolToColorMap = {}
	allSymbols.forEach((symbol, index) => {
		const hue = (index * 360) / allSymbols.length
		symbolToColorMap[symbol] = `hsl(${hue}, 70%, 50%)`
	})

	const CustomTooltip = ({ active, payload, label }) => {
		if (!active || !payload || payload.length === 0) return null

		const filtered = payload.filter(p => p.value > 0)

		return (
			<div
				style={{
					background: '#222',
					padding: '10px',
					borderRadius: '8px',
					border: '1px solid #555',
				}}
			>
				<p style={{ color: '#00ffc8', marginBottom: '5px' }}>{label}</p>
				{filtered.map((entry, index) => (
					<p
						key={index}
						style={{
							color: entry.color,
							fontFamily: 'Pixel, monospace',
							fontSize: '0.9rem',
							margin: 0,
						}}
					>
						{entry.name}: {entry.value}%
					</p>
				))}
			</div>
		)
	}

	return (
		<div ref={chartRef} className='detail-char'>
			<div className='chart'>
				<h1>
					<span>Whole</span> Last Year
				</h1>
				<div className='timeline-grid'>
					{[...Object.values(getAvailableMonthsByYear()).flat()]
						.sort((a, b) => b.year * 100 + b.month - (a.year * 100 + a.month))
						.map(({ month, year, label }) => {
							const isSelected = selectedMonths.some(
								m => m.month === month && m.year === year
							)

							const rowClass = year === 2025 ? 'row-1' : 'row-2'

							return (
								<button
									key={`${month}-${year}`}
									onClick={() => toggleMonth(month, year)}
									className={`menu-button timeline-button ${rowClass}`}
									style={{
										backgroundColor: isSelected ? '#00ffc8' : '',
									}}
								>
									{label}
								</button>
							)
						})}
				</div>
			</div>

			<div className='fullscreen-container'>
				<button onClick={toggleFullscreen} className='menu-button'>
					{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
				</button>
			</div>

			{loading ? (
				<div className='chart'>
					<Loading />
				</div>
			) : selectedMonths.length === 0 ? (
				<div className='chart'>
					<p>Please, choose your months...</p>
				</div>
			) : (
				selectedMonths.map(({ month, year }) => {
					const firstDay = new Date(year, month, 1)
					const lastDay = new Date(year, month + 1, 0)

					const monthData = portfolioData.filter(item => {
						const itemDate = new Date(item.time)
						return itemDate >= firstDay && itemDate <= lastDay
					})

					const latestPerDay = {}
					monthData.forEach(item => {
						const date = new Date(item.time).toISOString().split('T')[0]
						if (
							!latestPerDay[date] ||
							new Date(item.time) > new Date(latestPerDay[date].time)
						) {
							latestPerDay[date] = item
						}
					})

					const allSymbolsForMonth = new Set()
					Object.values(latestPerDay).forEach(item => {
						const currencies = JSON.parse(item.value)
						currencies.forEach(crypto => {
							allSymbolsForMonth.add(crypto.symbol)
						})
					})

					let mergedData = []
					Object.values(latestPerDay).forEach(item => {
						const currencies = JSON.parse(item.value)
						const date = new Date(item.time).toISOString().split('T')[0]
						const entry = { date }

						allSymbolsForMonth.forEach(symbol => {
							entry[symbol] = 0
						})

						currencies.forEach(crypto => {
							const value =
								typeof crypto.allocation === 'string'
									? parseFloat(crypto.allocation.replace('%', ''))
									: crypto.allocation
							entry[crypto.symbol] = value
						})

						mergedData.push(entry)
					})

					mergedData.sort((a, b) => new Date(a.date) - new Date(b.date))

					const keys = [...allSymbolsForMonth].filter(symbol =>
						mergedData.some(entry => entry[symbol] > 0)
					)

					return (
						<div key={`${month}-${year}`} className='chart'>
							<h2 className='text-lg font-bold mb-2'>
								{MONTHS[month].full} {year}
							</h2>
							<ResponsiveContainer width='100%' height={400}>
								<AreaChart data={mergedData}>
									<XAxis
										dataKey='date'
										tickFormatter={dateStr => {
											const [, month, day] = dateStr.split('-')
											return `${day}.${month}`
										}}
									/>
									<YAxis domain={[0, 100]} />
									<Tooltip content={<CustomTooltip />} />
									{keys.map(key => (
										<Area
											key={key}
											type='monotone'
											dataKey={key}
											stackId='1'
											stroke={symbolToColorMap[key]}
											fill={symbolToColorMap[key]}
										/>
									))}
								</AreaChart>
							</ResponsiveContainer>
						</div>
					)
				})
			)}
		</div>
	)
}

export default DetailedAnalysis
